/************************************************************************
 ** IBM Confidential
 **
 ** IBM Business Analytics: Rapidly Adaptive Visualization Engine
 **
 ** (C) Copyright IBM Corp. 2024
 **
 ** The source code for this program is not published or otherwise divested of its trade secrets, 
 ** irrespective of what has been deposited with the U.S. Copyright Office.
 ************************************************************************/
// GENERATED
import BaseMoserObject from './BaseMoserObject';
import ObjectFactory from './ObjectFactory';
import ReferenceResolver from './ReferenceResolver';
import MetricDefinitionHierarchyItem from './MetricDefinitionHierarchyItem';
import QuerySubject from './QuerySubject';
//@import BaseMoserObject (loadtime) // superclass
//@import com/ibm/bi/platform/moser/common/generated/metadata/ObjectFactory (runtime) // new
//@import com/ibm/bi/platform/moser/common/utils/ReferenceResolver (runtime) // getMoserObjectByIdForExpression
//@import com/ibm/bi/platform/moser/common/metric/hierarchy/MetricDefinitionHierarchyItem (static) // populateChildren
//@import com/ibm/bi/platform/moser/common/generated/metadata/QuerySubject (static) // fromJSON

/**
 * This class is used to represent a tree structure suggesting related query items to include in a metric definition. { "initialQueryItems": [ "INVENTORY_LEVELS.CLOSING_INVENTORY" ], "metricDefinitionHierarchyItemList": [ { "ref": "INVENTORY_LEVELS", "children": [ { "ref": "INVENTORY_LEVELS.CLOSING_INVENTORY" }, { "relationshipRef": "INVENTORY_LEVELS_PRODUCT", "ref": "PRODUCT", "children": [ { "ref": "PRODUCT.PRODUCTION_COST", "checked": true }, { "ref": "PRODUCT.MARGIN", "checked": true }, { "relationshipRef": "PRODUCT_PRODUCT_MULTILINGUAL", "ref": "PRODUCT_MULTILINGUAL", "children": [ { "ref": "PRODUCT_MULTILINGUAL.PRODUCT_NAME", "checked": true }, { "ref": "PRODUCT_MULTILINGUAL.DESCRIPTION", "checked": true }, { "ref": "PRODUCT_MULTILINGUAL.Lang_or_en" } ] }, { "relationshipRef": "PRODUCT_PRODUCT_TYPE", "ref": "PRODUCT_TYPE", "children": [ { "ref": "PRODUCT_TYPE.PRODUCT_TYPE_MS" }, { "relationshipRef": "PRODUCT_LINE_PRODUCT_TYPE", "ref": "PRODUCT_LINE", "children": [ { "ref": "PRODUCT_LINE.PRODUCT_LINE_FR" } ] } ] } ] } ] } ] }
 * @author  alan-tran
 */
export default class MetricDefinitionHierarchy extends BaseMoserObject{

	initialQueryItem = null;

	metricDefinitionHierarchyItemList = null;

	timeReference = null;

	timeGrain = null;

	timeAnchor = null;



	constructor() {
		super();
		this.setInitialQueryItem(null);
		this.setMetricDefinitionHierarchyItemList(null);
		this.setTimeReference(null);
		this.setTimeGrain(null);
		this.setTimeAnchor(null);
	}

	/**
	 * @return {Array}  the initialQueryItems
	 */
	getInitialQueryItem() {
		if (!this.initialQueryItem) {
			this.initialQueryItem = [];
		}
		return this.initialQueryItem;
	}

	/**
	 * @param  initialQueryItems  the initialQueryItems to set
	 */
	setInitialQueryItem(initialQueryItem) {
		this.initialQueryItem = initialQueryItem;
	}

	/**
	 * @return {Array}  the treeItems
	 */
	getMetricDefinitionHierarchyItemList() {
		if (!this.metricDefinitionHierarchyItemList) {
			this.metricDefinitionHierarchyItemList = [];
		}
		return this.metricDefinitionHierarchyItemList;
	}

	/**
	 * @param {Array} metricDefinitionHierarchyItemList  the metricDefinitionHierarchyItemList to set
	 */
	setMetricDefinitionHierarchyItemList(metricDefinitionHierarchyItemList) {
		this.metricDefinitionHierarchyItemList = metricDefinitionHierarchyItemList;
	}

	addMetricDefinitionHierarchyItemList(item) {
		this.getMetricDefinitionHierarchyItemList().push(item);
	}

	removeMetricDefinitionHierarchyItemList(item) {
		MetricDefinitionHierarchy._removeItem(this.getMetricDefinitionHierarchyItemList(), item);
	}

	/**
	 * @return {Array}  the timeReferences
	 */
	getTimeReference() {
		if (!this.timeReference) {
			this.timeReference = [];
		}
		return this.timeReference;
	}

	/**
	 * @param  timeReferences  the timeReferences to set
	 */
	setTimeReference(timeReference) {
		this.timeReference = timeReference;
	}

	addTimeReference(timeRef) {
		this.getTimeReference().push(timeRef);
	}

	removeTimeReference(timeRef) {
		MetricDefinitionHierarchy._removeItem(this.getTimeReference(), timeRef);
	}

	/**
	 * @return {Array}  the timeGrains
	 */
	getTimeGrain() {
		if (!this.timeGrain) {
			this.timeGrain = [];
		}
		return this.timeGrain;
	}

	/**
	 * @param {Array} timeGrains  the timeGrains to set
	 */
	setTimeGrain(timeGrains) {
		this.timeGrain = timeGrains;
	}

	static _removeItem(list, item) {
		if (!list) {
			return;
		}
		var i = list.indexOf(item);
		if (i >= 0) {
			list.splice(i, 1);
		}
	}

	addTimeGrain(timeGrainObj) {
		this.getTimeGrain().push(timeGrainObj);
	}

	removeTimeGrain(timeGrainObj) {
		MetricDefinitionHierarchy._removeItem(this.getTimeGrain(), timeGrainObj);
	}

	/**
	 * @return {TimeAnchor}  the timeAnchor
	 */
	getTimeAnchor() {
		return this.timeAnchor;
	}

	/**
	 * @param {TimeAnchor} timeAnchor  the timeAnchor to set
	 */
	setTimeAnchor(timeAnchor) {
		this.timeAnchor = timeAnchor;
	}

	clone() {
		var cloned = new MetricDefinitionHierarchy();
		cloned.setInitialQueryItem(this.cloneInitialQueryItem());
		cloned.setMetricDefinitionHierarchyItemList(this.cloneHierarchyItemList());
		cloned.setTimeGrain(this.cloneTimeGrain());
		cloned.setTimeAnchor(this.cloneTimeAnchor());
		var clonedTimeRef = [];
		for (var __i_enFor0 = 0, __exp_enFor0 = this.getTimeReference(), __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var timeRef = __exp_enFor0[__i_enFor0];
			clonedTimeRef.push(timeRef);
		}
		cloned.setTimeReference(clonedTimeRef);
		return cloned;
	}

	cloneTimeGrain() {
		var cloned = [];
		for (var __i_enFor0 = 0, __exp_enFor0 = this.getTimeGrain(), __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var timeGrainObj = __exp_enFor0[__i_enFor0];
			cloned.push(timeGrainObj);
		}
		return cloned;
	}

	cloneTimeAnchor() {
		if (this.getTimeAnchor()) {
			return this.getTimeAnchor().clone();
		}
		return null;
	}

	cloneInitialQueryItem() {
		var cloned = [];
		for (var __i_enFor0 = 0, __exp_enFor0 = this.getInitialQueryItem(), __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var qi = __exp_enFor0[__i_enFor0];
			cloned.push(qi);
		}
		return cloned;
	}

	cloneHierarchyItemList() {
		var cloned = [];
		for (var __i_enFor0 = 0, __exp_enFor0 = this.getMetricDefinitionHierarchyItemList(), __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var item = __exp_enFor0[__i_enFor0];
			cloned.push(item.clone());
		}
		return cloned;
	}

	toJSON() {
		var json = {};
		if (this.initialQueryItem) {
			var initialItems = [];
			for (var __i_enFor0 = 0, __exp_enFor0 = this.getInitialQueryItem(), __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var qi = __exp_enFor0[__i_enFor0];
				initialItems.push(qi.getIdForExpression());
			}
			json["initialQueryItems"] = initialItems;
		}
		if (this.metricDefinitionHierarchyItemList) {
			var treeItems = [];
			for (var __i_enFor1 = 0, __exp_enFor1 = this.getMetricDefinitionHierarchyItemList(), __len_enFor1 = __exp_enFor1.length;
					__i_enFor1 < __len_enFor1; ++__i_enFor1) {
				var item = __exp_enFor1[__i_enFor1];
				treeItems.push(item.toJSON());
			}
			json["metricDefinitionHierarchyItemList"] = treeItems;
		}
		if (this.timeReference) {
			var timeReferenceArr = [];
			for (var __i_enFor2 = 0, __exp_enFor2 = this.getTimeReference(), __len_enFor2 = __exp_enFor2.length;
					__i_enFor2 < __len_enFor2; ++__i_enFor2) {
				var timeRef = __exp_enFor2[__i_enFor2];
				timeReferenceArr.push(timeRef);
			}
			json["timeReference"] = timeReferenceArr;
		}
		if (this.timeGrain) {
			var timeGrainArr = [];
			for (var __i_enFor3 = 0, __exp_enFor3 = this.getTimeGrain(), __len_enFor3 = __exp_enFor3.length;
					__i_enFor3 < __len_enFor3; ++__i_enFor3) {
				var timeG = __exp_enFor3[__i_enFor3];
				timeGrainArr.push(timeG.toJSON());
			}
			json["timeGrain"] = timeGrainArr;
		}
		if (this.timeAnchor) {
			json["timeAnchor"] = this.getTimeAnchor().toJSON();
		}
		return json;
	}

	static fromJSON(metricNode, module) {
		var objectFactory = new ObjectFactory();
		var metric = new MetricDefinitionHierarchy();
		var initialQIsArray = metricNode["initialQueryItems"];
		var metricArray = metricNode["metricDefinitionHierarchyItemList"];
		var timeReference = metricNode["timeReference"];
		var timeGrain = metricNode["timeGrain"];
		var timeAnchorJSON = metricNode["timeAnchor"];
		metric.setInitialQueryItem(MetricDefinitionHierarchy._getInitialQIs(initialQIsArray, module));
		metric.setMetricDefinitionHierarchyItemList(MetricDefinitionHierarchy._getMetricItemList(metricArray, module));
		metric.setTimeReference(MetricDefinitionHierarchy._getTimeRefByJSON(timeReference));
		metric.setTimeGrain(MetricDefinitionHierarchy._getTimeGrainByJSON(timeGrain, objectFactory));
		metric.setTimeAnchor(MetricDefinitionHierarchy._getTimeAnchorByJSON(timeAnchorJSON, objectFactory));
		return metric;
	}

	static _getInitialQIs(initialQIsArray, module) {
		var initialQIs = [];
		for (var __i_enFor0 = 0, __exp_enFor0 = initialQIsArray, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var obj = __exp_enFor0[__i_enFor0];
			var objType = ReferenceResolver.getMoserObjectByIdForExpression(obj.toString(), module);
			initialQIs.push(objType);
		}
		return initialQIs;
	}

	static _getMetricItemList(metricArray, module) {
		return MetricDefinitionHierarchyItem.populateChildren(metricArray, module);
	}

	static _getTimeRefByJSON(timeReferences) {
		if (!timeReferences) {
			return [];
		}
		var timeRef = [];
		for (var __i_enFor0 = 0, __exp_enFor0 = timeReferences, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var obj = __exp_enFor0[__i_enFor0];
			timeRef.push(obj.toString());
		}
		return timeRef;
	}

	static _getTimeGrainByJSON(timeGrainArr, objectFactory) {
		if (!timeGrainArr) {
			return [];
		}
		var timeG = [];
		for (var __i_enFor0 = 0, __exp_enFor0 = timeGrainArr, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var timeGrainJSON = __exp_enFor0[__i_enFor0];
			var newTimeG = QuerySubject.TimeGrain.fromJSON(timeGrainJSON, objectFactory);
			timeG.push(newTimeG);
		}
		return timeG;
	}

	static _getTimeAnchorByJSON(timeAnchorJSON, objectFactory) {
		if (!timeAnchorJSON) {
			return null;
		}
		return QuerySubject.TimeAnchor.fromJSON(timeAnchorJSON, objectFactory);
	}
}


// WEBPACK FOOTER //
// ./src/generated/MetricDefinitionHierarchy.js